import { addNotification, clearNotification } from '@redux/reducers/notifications/notification.reducer';
import { addUser, clearUser } from '@redux/reducers/user/user.reducer';
import { avatarColors } from '@services/utils/static.data';

import millify from 'millify';

export class Utils {
  static avatarColor() {
    return avatarColors[Math.floor(Math.random() * 0.9 * avatarColors.length)];
  }

  static generateAvatar(text, backgroundColor, foregroundColor = 'white') {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = 200;
    canvas.height = 200;

    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Draw text
    context.font = 'normal 80px sans-serif';
    context.fillStyle = foregroundColor;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL('image/png');
  }

  static dispatchUser(result, pageReload, dispatch, setUser) {
    pageReload(true);
    dispatch(addUser({ token: result.data.token, profile: result.data.user }));
    setUser(result.data.user);
  }

  static clearStore({ dispatch, deleteStorageUsername, deleteSessionPageReload, setLoggedIn }) {
    dispatch(clearUser());
    dispatch(clearNotification());
    deleteStorageUsername();
    deleteSessionPageReload();
    setLoggedIn(false);
  }

  static dispatchNotification(message, type, dispatch) {
    dispatch(addNotification({ message, type }));
  }

  static dispatchClearNotification(dispatch) {
    dispatch(clearNotification());
  }

  static appEnvironment() {
    if (process.env.NODE_ENV !== 'production') {
      return 'LOCAL';
    }
    return '';
  }

  static mapSettingsDropdownItems(setSettings) {
    const items = [];
    const item = {
      topText: 'My Profile',
      subText: 'View personal profile.'
    };
    items.push(item);
    setSettings(items);
    return items;
  }

  static appImageUrl(version, id) {
    if (typeof version === 'string' && typeof id === 'string') {
      version = version.replace(/['"]+/g, '');
      id = id.replace(/['"]+/g, '');
    }
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    return `https://res.cloudinary.com/${cloudName}/image/upload/v${version}/${id}`;
  }

  static generateString(length) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = ' ';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  static checkIfUserIsBlocked(blocked, userId) {
    return blocked.some((id) => id === userId);
  }

  static checkIfUserIsFollowed(userFollowers, postCreatorId, userId) {
    return userFollowers.some((user) => user._id === postCreatorId || postCreatorId === userId);
  }

  static checkIfUserIsOnline(username, onlineUsers) {
    return onlineUsers.some((user) => user === username?.toLowerCase());
  }

  static firstLetterUpperCase(word) {
    if (!word) return '';
    return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
  }

  static formattedReactions(reactions) {
    const postReactions = [];
    for (const [key, value] of Object.entries(reactions)) {
      if (value > 0) {
        const reactionObject = {
          type: key,
          value
        };
        postReactions.push(reactionObject);
      }
    }
    return postReactions;
  }

  static shortenLargeNumbers(data) {
    if (data === undefined || data === null || typeof data !== 'number') {
      return 0;
    }
    return millify(data);
  }

  // Legacy documents in Mongo can store the literal string "undefined" (or "null")
  // instead of a real missing value, so a plain truthiness check on videoId/videoVersion
  // is not enough — it lets those strings through and produces a broken
  // ".../vundefined/undefined" Cloudinary URL.
  static isValidCloudinaryRef(value) {
    return Boolean(value) && value !== 'undefined' && value !== 'null';
  }

  static getImage(imageId, imageVersion) {
    return this.isValidCloudinaryRef(imageId) && this.isValidCloudinaryRef(imageVersion)
      ? this.appImageUrl(imageVersion, imageId)
      : '';
  }

  static getVideo(videoId, videoVersion) {
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    return this.isValidCloudinaryRef(videoId) && this.isValidCloudinaryRef(videoVersion)
      ? `https://res.cloudinary.com/${cloudName}/video/upload/v${videoVersion}/${videoId}`
      : '';
  }

  static removeUserFromList(list, userId) {
    const index = list.findIndex((id) => id === userId);
    list.splice(index, 1);
    return list;
  }

  static checkUrl(url, word) {
    return url.includes(word);
  }

  static renameFile(element) {
    const fileName = element.name.split('.').slice(0, -1).join('.');
    const blob = element.slice(0, element.size, 'image/png');
    const newFile = new File([blob], `${fileName}.png`, { type: 'image/png' });
    return newFile;
  }
}
